/**
 * API service pentru managementul body parts nomenclator și mapping-uri
 */
import api from './api';

// Types pentru body parts
export interface BodyPartsNomenclator {
  id: string;
  tenant_id: string;
  code: string;
  display_name: string;
  category: string;
  body_region: string;
  is_active: boolean;
  base_price?: number;
  created_at: string;
  updated_at: string;
}

export interface DicomBodyPartMapping {
  id: string;
  tenant_id: string;
  dicom_value: string;
  nomenclator_code?: string;
  modality: string;
  confidence: number;
  is_manual: boolean;
  series_description?: string;
  review_status: 'pending' | 'approved' | 'rejected' | 'auto_approved';
  reviewed_by?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface BodyPartsStats {
  total_nomenclator_items: number;
  active_nomenclator_items: number;
  total_mappings: number;
  pending_mappings: number;
  approved_mappings: number;
  studies_with_body_parts: number;
  studies_without_body_parts: number;
  categories: Record<string, number>;
  recent_extractions: number;
}

export interface StudyBodyParts {
  study_id: string;
  body_parts: string[];
  extraction_log?: any;
  last_extracted_at?: string;
}

// Request types
export interface NomenclatorCreateRequest {
  code: string;
  display_name: string;
  category: string;
  body_region: string;
  is_active?: boolean;
}

export interface NomenclatorUpdateRequest {
  display_name?: string;
  is_active?: boolean;
}

export interface ApproveMappingRequest {
  nomenclator_code: string;
  confidence?: number;
}

export interface RejectMappingRequest {
  reason?: string;
}

export interface BulkMappingRequest {
  modality: string;
  confidence: number;
  mappings: Array<{
    dicom_value: string;
    nomenclator_code: string;
  }>;
}

// Response types
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface MappingActionResponse {
  success: boolean;
  message: string;
  mapping_id: string;
  action: string;
}

export interface BulkMappingResponse {
  success: boolean;
  created_count: number;
  updated_count: number;
  errors: Array<{ dicom_value?: string; error: string }>;
  total_processed: number;
}

/**
 * Body Parts API Service
 */
class BodyPartsApiService {
  private baseUrl = '/body-parts';

  // Nomenclator Management - DEPRECATED: Folosește nomenclatorV2Api pentru v2
  // Aceste metode sunt păstrate pentru backward compatibility dar endpoint-urile nu mai există
  async getNomenclator(_params?: {
    category?: string;
    is_active?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<BodyPartsNomenclator>> {
    throw new Error('Endpoint deprecated. Use nomenclatorV2Api.listSegmentDefinitions() instead');
  }

  async createNomenclatorItem(_data: NomenclatorCreateRequest): Promise<BodyPartsNomenclator> {
    throw new Error('Endpoint deprecated. Use nomenclatorV2Api.createSegmentDefinition() instead');
  }

  async updateNomenclatorItem(_id: string, _data: NomenclatorUpdateRequest): Promise<BodyPartsNomenclator> {
    throw new Error('Endpoint deprecated. Use nomenclatorV2Api endpoints instead');
  }

  async deleteNomenclatorItem(_id: string): Promise<{ message: string }> {
    throw new Error('Endpoint deprecated. Use nomenclatorV2Api endpoints instead');
  }

  // Mapping Management - DEPRECATED: Use reviewMappingsApi instead
  // These endpoints have been moved to /api/v1/review-mappings
  async getPendingMappings(_params?: {
    modality?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<DicomBodyPartMapping>> {
    throw new Error('Endpoint deprecated. Use reviewMappingsApi.getPendingMappings() instead');
  }

  async approveMapping(_id: string, _data: ApproveMappingRequest): Promise<MappingActionResponse> {
    throw new Error('Endpoint deprecated. Use reviewMappingsApi.approveMapping() instead');
  }

  async rejectMapping(_id: string, _data: RejectMappingRequest): Promise<MappingActionResponse> {
    throw new Error('Endpoint deprecated. Use reviewMappingsApi.rejectMapping() instead');
  }

  async createBulkMappings(data: BulkMappingRequest): Promise<BulkMappingResponse> {
    const response = await api.post(`${this.baseUrl}/mappings/bulk`, data);
    return response.data;
  }

  // Study Body Parts
  async getStudyBodyParts(studyId: string): Promise<StudyBodyParts> {
    const response = await api.get(`${this.baseUrl}/studies/${studyId}/body-parts`);
    return response.data;
  }

  async extractStudyBodyParts(studyId: string, forceRefresh = false): Promise<StudyBodyParts> {
    const response = await api.post(`${this.baseUrl}/studies/${studyId}/body-parts/extract`, {
      force_refresh: forceRefresh
    });
    return response.data;
  }

  // Statistics
  async getStats(): Promise<BodyPartsStats> {
    const response = await api.get(`${this.baseUrl}/stats`);
    return response.data;
  }
}

export const bodyPartsApi = new BodyPartsApiService();
